import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import { getAppConfig } from "@/config/app-config";

enum StatusCode {
  Unauthorized = 401,
  Forbiden = 403,
  TooManyRequests = 429,
  InternalServerError = 500,
  MovedPermanently = 301,
}

const headers: Readonly<Record<string, string | boolean>> = {
  Accept: "application/json",
  "Content-Type": "application/json;",
  "X-requested-With": "XMLHttpRequest",
};
const publicPaths = ["/refund_preload/"];

function isPublicRequest(url?: string): boolean {
  if (!url) return false;
  const path = url.split("?")[0];
  return publicPaths.some((publicPath) => path.includes(publicPath));
}

const injectToken = (
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
  try {
    const token = localStorage.getItem("token");
    if (token && !isPublicRequest(config.url)) {
      config.headers["Authorization"] = `JWT ${token}`;
    }
    return config;
  } catch (error: any) {
    throw new Error(error);
  }
};

class Http {
  private instance: AxiosInstance | null = null;

  private get http(): AxiosInstance {
    return this.instance != null ? this.instance : this.initHttp();
  }

  initHttp() {
    const http = axios.create({
      baseURL: getAppConfig().baseUrl,
      headers,
    });

    http.interceptors.request.use(injectToken, (error) =>
      Promise.reject(error)
    );

    http.interceptors.response.use(
      (response) => response,
      (error) => {
        const { response } = error;
        return this.handleError(response);
      }
    );

    this.instance = http;
    return http;
  }

  request<T = any, R = AxiosResponse<T>>(
    config: AxiosRequestConfig
  ): Promise<R> {
    return this.http.request<T, R>(config) as Promise<R>;
  }

  get<T = any, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.http.get<T, R>(url, config) as Promise<R>;
  }

  post<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: T,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.http.post<T, R>(url, data, config) as Promise<R>;
  }

  put<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: T,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.http.put<T, R>(url, data, config) as Promise<R>;
  }

  patch<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: T,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.http.patch<T, R>(url, data, config) as Promise<R>;
  }

  delete<T = any, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.http.delete<T, R>(url, config) as Promise<R>;
  }

  setBaseURL(url: string) {
    if (this.instance) {
      this.instance.defaults.baseURL = url;
    }
  }

  private handleError(error: any) {
    const { status, config } = error;
    const requestUrl = String(config?.url ?? "");

    switch (status) {
      case StatusCode.InternalServerError: {
        break;
      }
      case StatusCode.Forbiden: {
        break;
      }
      case StatusCode.Unauthorized: {
        if (requestUrl.includes("/autologin")) {
          break;
        }
        localStorage.clear();
        localStorage.setItem("sesionExpired", "true");
        location.replace("/login");
        break;
      }
      case StatusCode.TooManyRequests: {
        // Handle TooManyRequests
        break;
      }
      case StatusCode.MovedPermanently: {
        break;
      }
      default:
        break;
    }

    return Promise.reject(error);
  }
}

export const axiosInstance = new Http();
